import { useEffect, useRef, useState, useCallback } from "react";
import { Eraser, Trash2, Code2, PenTool, StickyNote, X, ThumbsUp, ArrowDownWideNarrow } from "lucide-react";
import api from "@/lib/api";
import { Avatar } from "@/components/common";
import { CodeEditorField, LANGUAGES, CODE_LINE_HEIGHT, CODE_PADDING } from "@/components/CodeEditorField";
import { useAuth } from "@/context/AuthContext";

const W = 1000, H = 625;
const COLORS = ["#111827", "#3157D5", "#20B486", "#C98B4A", "#DC2626", "#7C3AED"];
const CURSOR_COLORS = ["#3157D5", "#20B486", "#C98B4A", "#DC2626", "#7C3AED", "#0891B2", "#DB2777"];
const colorFor = (id) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return CURSOR_COLORS[h % CURSOR_COLORS.length];
};

function Canvas({ projectId }) {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawing = useRef(false);
  const cur = useRef([]);
  const strokes = useRef([]);
  const lastSeq = useRef(0);
  const version = useRef(-1);
  const lastCursorSent = useRef(0);
  const [color, setColor] = useState(COLORS[1]);
  const [size, setSize] = useState(3);
  const [erase, setErase] = useState(false);
  const [presence, setPresence] = useState([]);
  const [cursors, setCursors] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteEditors, setNoteEditors] = useState([]);
  const [sortByVotes, setSortByVotes] = useState(false);
  const draggingNote = useRef(null);
  const notesRef = useRef([]);
  useEffect(() => { notesRef.current = notes; }, [notes]);

  const drawStroke = useCallback((ctx, s) => {
    if (!s.points || s.points.length === 0) return;
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.size;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(s.points[0][0], s.points[0][1]);
    for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i][0], s.points[i][1]);
    if (s.points.length === 1) ctx.lineTo(s.points[0][0] + 0.1, s.points[0][1] + 0.1);
    ctx.stroke();
  }, []);

  const redrawAll = useCallback(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    strokes.current.forEach((s) => drawStroke(ctx, s));
  }, [drawStroke]);

  const poll = useCallback(async () => {
    try {
      const r = await api.get(`/build/projects/${projectId}/whiteboard`, {
        params: { since: lastSeq.current, version: version.current },
      });
      setPresence(r.data.presence || []);
      setNoteEditors(r.data.note_editors || []);
      setNotes((prev) => {
        // don't clobber a note currently being dragged/edited locally
        const incoming = r.data.notes || [];
        return incoming.map((n) => {
          const local = prev.find((p) => p.note_id === n.note_id);
          if (local && draggingNote.current === n.note_id) return { ...n, x: local.x, y: local.y };
          return n;
        });
      });
      if (r.data.reset) {
        version.current = r.data.version;
        strokes.current = r.data.strokes;
        lastSeq.current = r.data.last_seq;
        redrawAll();
      } else if (r.data.strokes.length) {
        const ctx = canvasRef.current.getContext("2d");
        r.data.strokes.forEach((s) => { strokes.current.push(s); drawStroke(ctx, s); });
        lastSeq.current = r.data.last_seq;
      }
    } catch (e) {}
  }, [projectId, redrawAll, drawStroke]);

  useEffect(() => {
    redrawAll();
    poll();
    const t = setInterval(poll, 1300);
    return () => clearInterval(t);
  }, [poll, redrawAll]);

  const pollCursors = useCallback(async () => {
    try {
      const r = await api.get(`/build/projects/${projectId}/whiteboard/cursors`);
      setCursors(r.data.cursors || []);
    } catch (e) {}
  }, [projectId]);

  useEffect(() => {
    const t = setInterval(pollCursors, 500);
    return () => clearInterval(t);
  }, [pollCursors]);

  const sendCursor = (p) => {
    const now = Date.now();
    if (now - lastCursorSent.current < 180) return;
    lastCursorSent.current = now;
    api.post(`/build/projects/${projectId}/whiteboard/cursor`, { x: p[0], y: p[1] }).catch(() => {});
  };
  const sendActivity = (patch) => api.post(`/build/projects/${projectId}/whiteboard/presence`, patch).catch(() => {});

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return [((e.clientX - rect.left) / rect.width) * W, ((e.clientY - rect.top) / rect.height) * H];
  };

  const start = (e) => { drawing.current = true; cur.current = [pos(e)]; };
  const move = (e) => {
    const p = pos(e);
    sendCursor(p);
    if (!drawing.current) return;
    const last = cur.current[cur.current.length - 1];
    if (last && Math.hypot(p[0] - last[0], p[1] - last[1]) < 2) return;
    cur.current.push(p);
    const ctx = canvasRef.current.getContext("2d");
    drawStroke(ctx, { points: cur.current.slice(-2), color: erase ? "#ffffff" : color, size: erase ? size * 4 : size });
  };
  const end = async () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (cur.current.length === 0) return;
    const stroke = { points: cur.current, color: erase ? "#ffffff" : color, size: erase ? size * 4 : size };
    cur.current = [];
    strokes.current.push(stroke);
    try {
      const r = await api.post(`/build/projects/${projectId}/whiteboard/stroke`, stroke);
      lastSeq.current = Math.max(lastSeq.current, r.data.seq);
    } catch (e) {}
  };

  const clear = async () => {
    if (!window.confirm("Clear the whole canvas for everyone?")) return;
    await api.post(`/build/projects/${projectId}/whiteboard/clear`);
    strokes.current = [];
    redrawAll();
    poll();
  };

  const NOTE_COLORS = ["#FEF08A", "#BBF7D0", "#BFDBFE", "#FBCFE8"];
  const addNote = async () => {
    const jx = (Math.random() - 0.5) * 220, jy = (Math.random() - 0.5) * 200;
    const r = await api.post(`/build/projects/${projectId}/whiteboard/note`,
      { text: "", x: W / 2 - 60 + jx, y: H / 2 - 60 + jy, color: NOTE_COLORS[Math.floor(Math.random() * NOTE_COLORS.length)] });
    setNotes((prev) => [...prev, { ...r.data, votes: r.data.votes || [] }]);
  };
  const patchNote = (id, patch) => setNotes((prev) => prev.map((n) => n.note_id === id ? { ...n, ...patch } : n));
  const saveNote = (id, patch) => api.put(`/build/projects/${projectId}/whiteboard/note/${id}`, patch).catch(() => {});
  const removeNote = async (id) => {
    setNotes((prev) => prev.filter((n) => n.note_id !== id));
    await api.delete(`/build/projects/${projectId}/whiteboard/note/${id}`).catch(() => {});
  };
  const voteNote = async (id) => {
    if (!user?.user_id) return;
    const n = notesRef.current.find((x) => x.note_id === id);
    const has = (n?.votes || []).includes(user?.user_id);
    const optimistic = has ? (n.votes || []).filter((v) => v !== user?.user_id) : [...(n?.votes || []), user?.user_id];
    patchNote(id, { votes: optimistic });
    try {
      const r = await api.post(`/build/projects/${projectId}/whiteboard/note/${id}/vote`);
      patchNote(id, { votes: r.data.votes });
    } catch (e) {}
  };

  const startDrag = (e, note) => {
    if (sortByVotes) return;
    e.stopPropagation();
    draggingNote.current = note.note_id;
    const rect = wrapRef.current.getBoundingClientRect();
    const grabX = ((e.clientX - rect.left) / rect.width) * W - note.x;
    const grabY = ((e.clientY - rect.top) / rect.height) * H - note.y;
    const onMove = (ev) => {
      const nx = ((ev.clientX - rect.left) / rect.width) * W - grabX;
      const ny = ((ev.clientY - rect.top) / rect.height) * H - grabY;
      patchNote(note.note_id, { x: Math.max(0, Math.min(W - 40, nx)), y: Math.max(0, Math.min(H - 40, ny)) });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const cur = notesRef.current.find((n) => n.note_id === note.note_id);
      if (cur) saveNote(note.note_id, { x: cur.x, y: cur.y });
      draggingNote.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // editors grouped by note
  const editorsByNote = {};
  noteEditors.forEach((e) => {
    (editorsByNote[e.note_id] = editorsByNote[e.note_id] || []).push(e);
  });

  // display positions (sort-by-votes lays notes out in a top-anchored grid)
  const perRow = Math.max(1, Math.floor((W - 16) / 148));
  const ranked = [...notes].sort((a, b) => (b.votes?.length || 0) - (a.votes?.length || 0));
  const displayNotes = sortByVotes
    ? ranked.map((n, i) => ({ ...n, x: 16 + (i % perRow) * 148, y: 16 + Math.floor(i / perRow) * 152 }))
    : notes;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center gap-1.5">
          {COLORS.map((c) => (
            <button key={c} onClick={() => { setColor(c); setErase(false); }} data-testid={`wb-color-${c}`}
              className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${color === c && !erase ? "border-foreground" : "border-transparent"}`}
              style={{ backgroundColor: c }} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input type="range" min={1} max={12} value={size} onChange={(e) => setSize(Number(e.target.value))} data-testid="wb-size" className="w-24" />
          <span className="text-xs font-mono text-muted-foreground">{size}px</span>
        </div>
        <button onClick={() => setErase((v) => !v)} data-testid="wb-eraser"
          className={`inline-flex items-center gap-1.5 border rounded-md px-3 py-1.5 text-sm transition-colors ${erase ? "border-cobalt text-cobalt" : "border-border hover:bg-secondary/60"}`}>
          <Eraser size={14} /> Eraser
        </button>
        <button onClick={clear} data-testid="wb-clear"
          className="inline-flex items-center gap-1.5 border border-destructive/40 text-destructive rounded-md px-3 py-1.5 text-sm hover:bg-destructive/10 transition-colors">
          <Trash2 size={14} /> Clear
        </button>
        <button onClick={addNote} data-testid="wb-add-note"
          className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-sm hover:bg-secondary/60 transition-colors">
          <StickyNote size={14} /> Note
        </button>
        <button onClick={() => setSortByVotes((v) => !v)} data-testid="wb-sort-votes"
          className={`inline-flex items-center gap-1.5 border rounded-md px-3 py-1.5 text-sm transition-colors ${sortByVotes ? "border-cobalt text-cobalt" : "border-border hover:bg-secondary/60"}`}>
          <ArrowDownWideNarrow size={14} /> Sort by votes
        </button>
        <div className="ml-auto flex items-center -space-x-2" data-testid="wb-presence">
          {presence.slice(0, 6).map((p) => (
            <span key={p.user_id} title={p.name} className="ring-2 ring-card rounded-full">
              <Avatar src={p.picture} name={p.name} size={26} />
            </span>
          ))}
          <span className="ml-3 text-xs text-muted-foreground font-mono">{presence.length} live</span>
        </div>
      </div>
      <div ref={wrapRef} className="relative">
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          data-testid="whiteboard-canvas"
          className="w-full border border-border rounded-xl bg-white touch-none cursor-crosshair block"
          style={{ aspectRatio: `${W}/${H}` }}
        />
        {cursors.map((c) => (
          <div key={c.user_id} data-testid={`live-cursor-${c.user_id}`}
            className="absolute pointer-events-none z-10 transition-all duration-200 ease-linear"
            style={{ left: `${(c.x / W) * 100}%`, top: `${(c.y / H) * 100}%` }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill={colorFor(c.user_id)} style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,.3))" }}>
              <path d="M4 2l6 16 2.5-6.5L19 9z" />
            </svg>
            <span className="ml-3 -mt-1 inline-block text-[10px] font-medium text-white px-1.5 py-0.5 rounded"
              style={{ backgroundColor: colorFor(c.user_id) }}>{c.name}</span>
          </div>
        ))}
        {displayNotes.map((n) => {
          const editors = editorsByNote[n.note_id] || [];
          const votes = n.votes || [];
          const mine = votes.includes(user?.user_id);
          return (
          <div key={n.note_id} data-testid={`sticky-note-${n.note_id}`}
            className="absolute z-20 w-32 shadow-md rounded-sm flex flex-col"
            style={{ left: `${(n.x / W) * 100}%`, top: `${(n.y / H) * 100}%`, backgroundColor: n.color,
                     transition: sortByVotes ? "left .3s ease, top .3s ease" : "none" }}>
            <div onPointerDown={(e) => startDrag(e, n)} data-testid={`sticky-drag-${n.note_id}`}
              className={`h-6 flex items-center justify-between px-1 bg-black/5 rounded-t-sm ${sortByVotes ? "" : "cursor-grab active:cursor-grabbing"}`}>
              <div className="flex items-center -space-x-1.5" data-testid={`sticky-editors-${n.note_id}`}>
                {editors.slice(0, 3).map((ed) => (
                  <span key={ed.user_id} title={`${ed.name} is editing`} className="ring-2 ring-white/70 rounded-full animate-pulse">
                    <Avatar src={ed.picture} name={ed.name} size={16} />
                  </span>
                ))}
              </div>
              <button onClick={() => removeNote(n.note_id)} data-testid={`sticky-delete-${n.note_id}`}
                className="text-black/40 hover:text-destructive"><X size={12} /></button>
            </div>
            <textarea
              key={n.updated_at}
              defaultValue={n.text}
              onFocus={() => sendActivity({ editing_note: n.note_id })}
              onBlur={(e) => { saveNote(n.note_id, { text: e.target.value }); sendActivity({ editing_note: null }); }}
              placeholder="Idea…"
              data-testid={`sticky-text-${n.note_id}`}
              className="flex-1 bg-transparent resize-none outline-none text-xs p-2 text-gray-800 min-h-[68px]"
            />
            <button onClick={() => voteNote(n.note_id)} data-testid={`sticky-vote-${n.note_id}`}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-b-sm border-t border-black/10 transition-colors ${mine ? "text-cobalt font-semibold bg-black/5" : "text-gray-600 hover:bg-black/5"}`}>
              <ThumbsUp size={12} fill={mine ? "currentColor" : "none"} />
              <span data-testid={`sticky-vote-count-${n.note_id}`}>{votes.length}</span>
            </button>
          </div>
        );})}
      </div>
    </div>
  );
}

function SharedCode({ projectId }) {
  const [code, setCode] = useState("");
  const [lang, setLang] = useState("javascript");
  const [editedBy, setEditedBy] = useState(null);
  const [codePresence, setCodePresence] = useState([]);
  const [scrollTop, setScrollTop] = useState(0);
  const [editorH, setEditorH] = useState(360);
  const focused = useRef(false);
  const lastAt = useRef(null);
  const saveTimer = useRef(null);
  const myLine = useRef(null);
  const wrapRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const r = await api.get(`/build/projects/${projectId}/whiteboard`, { params: { since: 9e9, version: -2 } });
      setCodePresence(r.data.code_presence || []);
      if (!focused.current && r.data.code_at && r.data.code_at !== lastAt.current) {
        setCode(r.data.code || "");
        setLang(r.data.code_language || "javascript");
        lastAt.current = r.data.code_at;
        setEditedBy(r.data.code_by);
      }
    } catch (e) {}
  }, [projectId]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 2000);
    return () => clearInterval(t);
  }, [poll]);

  const sendActivity = useCallback((patch) => api.post(`/build/projects/${projectId}/whiteboard/presence`, patch).catch(() => {}), [projectId]);

  useEffect(() => {
    return () => sendActivity({ code_line: null });
  }, [sendActivity]);

  useEffect(() => {
    const ta = wrapRef.current?.querySelector("textarea");
    if (!ta) return;
    const onScroll = () => setScrollTop(ta.scrollTop);
    ta.addEventListener("scroll", onScroll);
    setEditorH(ta.clientHeight || 360);
    return () => ta.removeEventListener("scroll", onScroll);
  }, [code]);

  const onCaretLine = (line) => {
    if (line === myLine.current) return;
    myLine.current = line;
    sendActivity({ code_line: line });
  };

  const save = (nextCode, nextLang) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/build/projects/${projectId}/whiteboard/code`, { code: nextCode, code_language: nextLang })
        .then(() => { lastAt.current = new Date().toISOString(); });
    }, 700);
  };

  // group teammates by line
  const byLine = {};
  codePresence.forEach((p) => { (byLine[p.line] = byLine[p.line] || []).push(p); });

  return (
    <div onFocus={() => (focused.current = true)} onBlur={() => (focused.current = false)}>
      <div className="flex items-center justify-between mb-3">
        <select value={lang} onChange={(e) => { setLang(e.target.value); save(code, e.target.value); }} data-testid="shared-code-lang"
          className="bg-transparent border border-border rounded-md px-3 py-1.5 text-sm outline-none">
          {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        {editedBy && <span className="text-xs text-muted-foreground">last edited by {editedBy}</span>}
      </div>
      <div className="flex">
        <div className="relative w-9 shrink-0" style={{ height: editorH }} data-testid="code-presence-gutter">
          {Object.entries(byLine).map(([line, people]) => {
            const top = CODE_PADDING + (Number(line) - 1) * CODE_LINE_HEIGHT - scrollTop;
            if (top < -2 || top > editorH - 6) return null;
            return (
              <div key={line} className="absolute right-1 flex -space-x-2" style={{ top }}
                title={people.map((p) => `${p.name} · line ${line}`).join(", ")}
                data-testid={`code-presence-line-${line}`}>
                {people.slice(0, 3).map((p) => (
                  <span key={p.user_id} className="ring-2 ring-card rounded-full">
                    <Avatar src={p.picture} name={p.name} size={20} />
                  </span>
                ))}
              </div>
            );
          })}
        </div>
        <div ref={wrapRef} className="flex-1 min-w-0">
          <CodeEditorField value={code} language={lang} minHeight={360} onCaretLine={onCaretLine}
            onChange={(v) => { setCode(v); save(v, lang); }} />
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Shared pad — everyone edits the same code. Avatars show which line teammates are on.</p>
    </div>
  );
}

export function ProjectWhiteboard({ projectId }) {
  const [mode, setMode] = useState("canvas");
  return (
    <div className="border border-border bg-card rounded-xl p-5" data-testid="project-whiteboard">
      <div className="flex gap-1 border-b border-border mb-4">
        <button onClick={() => setMode("canvas")} data-testid="wb-mode-canvas"
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${mode === "canvas" ? "border-cobalt text-cobalt" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <PenTool size={15} /> Canvas
        </button>
        <button onClick={() => setMode("code")} data-testid="wb-mode-code"
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${mode === "code" ? "border-cobalt text-cobalt" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <Code2 size={15} /> Shared code
        </button>
      </div>
      {mode === "canvas" ? <Canvas projectId={projectId} /> : <SharedCode projectId={projectId} />}
    </div>
  );
}
